// Pure, framework-free helpers for the AI model-config Create/Edit form (sub-plan 20). Kept out of
// the React components so the JSON validate / format / transform logic is unit-testable in isolation
// (REF-01 s.1.1 field shapes; sub-plan 17 model-config endpoints consume the parsed params object).

// Provider CHECK set (REF-01 s.1.1). `zhipu` = GLM, `alibaba` = Qwen. Labels show the friendly name.
export const MODEL_PROVIDERS = [
  'anthropic',
  'openai',
  'google',
  'zhipu',
  'alibaba',
  'cloudflare',
] as const;

export type ModelProvider = (typeof MODEL_PROVIDERS)[number];

// Friendly provider labels (the SelectInput shows these; the stored value stays the code).
export const PROVIDER_LABELS: Record<ModelProvider, string> = {
  anthropic: 'Anthropic',
  openai: 'OpenAI',
  google: 'Google',
  zhipu: 'Zhipu (GLM)',
  alibaba: 'Alibaba (Qwen)',
  cloudflare: 'Cloudflare',
};

// Role CHECK set (REF-01 s.1.1).
export const MODEL_ROLES = ['investigation', 'gate'] as const;
export type ModelRole = (typeof MODEL_ROLES)[number];

// Server-managed fields that must NEVER be in the form payload (display only). The transform strips
// them so a client cannot rewrite timestamps/ids.
export const READ_ONLY_FIELDS = ['id', 'createdAt', 'updatedAt'] as const;

const isBlank = (s: unknown): boolean =>
  s === null || s === undefined || (typeof s === 'string' && s.trim() === '');

// Pretty-print a JSON value into the string the monospace TextInput displays. Used as the `format`
// of the params input. null/undefined/'' -> '{}' so the field shows an empty object, not "null".
export const formatParams = (value: unknown): string => {
  if (value === null || value === undefined || value === '') return '{}';
  if (typeof value === 'string') return value; // already a string (mid-edit) — show as-is
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return '{}';
  }
};

// The `parse` of the params input keeps the raw STRING in form state (so the user keeps typing
// freely and `validate` can run on the string). String->object conversion happens in `transform`.
export const parseParamsInput = (value: string): string => value;

// react-admin field validator: the string must parse as a JSON OBJECT. Returns the i18n error KEY on
// failure (react-admin translates it), or undefined when valid/blank.
export const validateParamsJson = (value: unknown): string | undefined => {
  if (isBlank(value)) return undefined; // blank -> {} in transform
  try {
    const parsed = JSON.parse(value as string);
    if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return 'resources.ai-model-configs.errors.paramsNotObject';
    }
    return undefined;
  } catch {
    return 'resources.ai-model-configs.errors.invalidJson';
  }
};

// React Admin Create/Edit `transform`: JSON-parse `params` text -> object (blank -> {}); coerce
// `gatewayRoute === '' -> undefined` (REF-01: null = default); pass booleans through; strip
// read-only fields. Throws on invalid params JSON so the form surfaces the error instead of POSTing
// a bad body (no silent default {} substitution that would discard the operator's intended params).
export const transformModelConfig = <T extends Record<string, any>>(
  data: T,
): Record<string, any> => {
  const out: Record<string, any> = { ...data };

  for (const f of READ_ONLY_FIELDS) {
    delete out[f];
  }

  // gatewayRoute: blank -> undefined (omitted -> backend stores null = default).
  if (isBlank(out.gatewayRoute)) {
    out.gatewayRoute = undefined;
  }

  // params: blank -> {}; otherwise parse the string to an object. The validator blocks submit on
  // invalid JSON before this runs, but parse defensively here too (throws surface as a form error).
  if (isBlank(out.params)) {
    out.params = {};
  } else if (typeof out.params === 'string') {
    out.params = JSON.parse(out.params);
  }

  return out;
};
